/* eslint-disable func-names */
import { AxiosInstance, AxiosRequestConfig } from 'axios'
import { filter, find } from 'lodash'
import { toast } from 'react-toastify'
import { AnyAction } from 'redux'
import { all, put, select, takeEvery } from 'redux-saga/effects'
import { v4 as uuid } from 'uuid'

import { getPageOfIndex, requestPage } from '../sagaUtils'
import { IActionsMap } from './actions'
import { TConstantMap } from './constants'
import { ISelectorProps } from './selectors'

interface ISagaProps {
  constants: TConstantMap
  actions: IActionsMap
  selectors: ISelectorProps
  url: string
  customAxiosClient?: ({ ...options }: AxiosRequestConfig) => Promise<any>
}
type TReturnAnyGenerator = Generator<any, any, any>

export function generateSaga(props: ISagaProps): any {
  const { constants, actions, selectors, url } = props

  const {
    receive,
    clearFilters,
    applyLocalFulltextfilter,
    setItems,
    sortLocal,
    applyLocalValueFilter,
    setLoading,
    applyLocalFilter,
    setRequestId,
  } = actions

  const {
    REQUEST,
    APPLY_FILTER,
    APPLY_LOCAL_FILTER,
    APPLY_LOCAL_SORT,
    SET_FILTER,
  } = constants

  const {
    selectLoadedPages,
    selectFilterClean,
    selectIncludedFilters,
    selectItemsToRestore,
    selectFilterMode,
    selectUseLocalStateFiltering,
    selectCurrentRequestId,
    selectCustomHeaders,
    selectCustomPayload,
  } = selectors

  let loadingPages = {}

  const request = function* ({
    payload: { startIndex, stopIndex, requestId },
  }: AnyAction): TReturnAnyGenerator {
    try {
      const pageStart = getPageOfIndex(startIndex)
      const pageStop = getPageOfIndex(stopIndex)

      const passedRequestId = requestId
        ? requestId
        : yield select(selectCurrentRequestId)
      // Get params

      const customHeaders = yield select(selectCustomHeaders)
      const customPayload = yield select(selectCustomPayload)
      const loadedPages = yield select(selectLoadedPages)
      let filter = yield select(selectFilterClean)
      const includedFilters = yield select(selectIncludedFilters)

      // add included filter options to each value of filter.filter
      filter = {
        ...filter,
        filter: filter.filter.map((field) => {
          return { ...field, extra: includedFilters }
        }),
      }
      // Load all pages
      for (let i = pageStart; i <= pageStop; i++) {
        // skip already loaded
        if (loadedPages[i]) {
          continue
        }

        if (loadingPages[i]) {
          continue
        } else {
          loadingPages[i] = true
          const result = yield requestPage(
            url,
            i,
            filter,
            customHeaders,
            customPayload,
          )

          const currentRequestId = yield select(selectCurrentRequestId)

          if (passedRequestId !== currentRequestId) {
            break
          } else {
            yield put(receive(result))
          }
        }
      }
    } catch (error) {
      console.error(error)
      toast.error(i18n.t('error_fetching_data', 'Fehler beim Abruf der Daten'))
      yield put(receive(error))
    }
  }

  const applyFilter = function* ({
    payload: {
      reset: { reset, onlyFts },
    },
  }: AnyAction): TReturnAnyGenerator {
    const id = uuid()
    yield put(setRequestId(id))

    loadingPages = {}
    // eslint-disable-next-line no-extra-boolean-cast
    if (reset) {
      yield put(clearFilters({ onlyFts }))
    }
    yield request({
      payload: { startIndex: 0, stopIndex: 1, requestId: id },
      type: 'request',
    })
  }

  const handleApplyLocalFilter = function* ({
    payload: { reset, onlyFts },
  }: AnyAction): TReturnAnyGenerator {
    yield put(setLoading(true))
    if (reset) {
      yield put(clearFilters({ onlyFts }))
      yield put(setItems(yield select(selectItemsToRestore)))
      yield put(setLoading(false))
    } else {
      const cleanFilters = yield select(selectFilterClean)
      const filterMode = yield select(selectFilterMode)

      const fullTextFilter = find(
        cleanFilters.filter,
        (filter) => filter.field === 'fulltext',
      )

      const valueFilter = filter(
        cleanFilters.filter,
        (filter) => filter.field !== 'fulltext',
      )

      if (filterMode === 'fulltext' && fullTextFilter) {
        yield put(applyLocalFulltextfilter({ fullTextFilter }))
      } else {
        yield put(applyLocalValueFilter({ valueFilter }))
      }
      yield put(setLoading(false))
    }
  }

  const applyLocalSort = function* (): TReturnAnyGenerator {
    yield put(setLoading(true))
    yield put(sortLocal())
    yield put(setLoading(false))
  }

  const applyInstantFtsFilter = function* (): TReturnAnyGenerator {
    const filterMode = yield select(selectFilterMode)
    const useLocalFiltering = yield select(selectUseLocalStateFiltering)

    if (useLocalFiltering && filterMode === 'fulltext') {
      yield put(applyLocalFilter({ reset: false, onlyFts: false }))
    }
  }

  return function* root() {
    yield all([
      takeEvery(REQUEST, request),
      takeEvery(APPLY_FILTER, applyFilter),
      takeEvery(APPLY_LOCAL_FILTER, handleApplyLocalFilter),
      takeEvery([APPLY_LOCAL_SORT], applyLocalSort),
      takeEvery([SET_FILTER], applyInstantFtsFilter),
    ])
  }
}
